import React, { useRef, useEffect, useState } from "react";
import {
    Box,
    IconButton,
    Avatar,
    Typography,
    Tooltip,
} from "@mui/material";
import { AddAPhoto, Close } from "@mui/icons-material";
import { SxProps } from "@mui/system";
import { useSnackBar } from "../ToastProvider";

interface ImageUploaderProps {
    images: (File | string)[] | null;
    onChange: (files: (File | string)[]) => void;
    multiple?: boolean;
    maxCount?: number;
    showRemoveButton?: boolean;
    disabled?: boolean;
    size?: number;
    sx?: SxProps;
    onImageClick?: (index: number) => void;

    allowedExtensions?: string[];
    accept?: string;
    maxFileSize?: number;
    readOnly?: boolean;
    onRemoveRequest?: (index: number) => void;
}

const DEFAULT_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const DEFAULT_MAX_SIZE = 5 * 1000 * 1000; // 5,000,000 bytes (5MB decimal matching OS file size)
const FALLBACK_IMG = "/default.jpg";

const ImageUploader: React.FC<ImageUploaderProps> = ({
    images,
    onChange,
    multiple = false,
    maxCount = 5,
    showRemoveButton = true,
    disabled = false,
    size = 100,
    sx,
    onImageClick,

    allowedExtensions = DEFAULT_EXTENSIONS,
    accept = ".jpg, .jpeg, .png, image/jpeg, image/png",
    maxFileSize = DEFAULT_MAX_SIZE,
    readOnly = false,
    onRemoveRequest,
}) => {
    const { showSnackBar } = useSnackBar();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [failed, setFailed] = useState<Record<string, boolean>>({});
    const fileToBlobRef = useRef<WeakMap<File, string>>(new WeakMap());
    const blobUrlsRef = useRef<string[]>([]);
    const isReadOnly = Boolean(disabled || readOnly);

    const getImageUrl = (item: File | string): string => {
        if (typeof item === "string") return item;
        let url = fileToBlobRef.current.get(item);
        if (!url) {
            url = URL.createObjectURL(item);
            fileToBlobRef.current.set(item, url);
            blobUrlsRef.current.push(url);
        }
        return url;
    };

    // Cleanup blob URLs ONLY when the component unmounts
    useEffect(() => {
        return () => {
            blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            blobUrlsRef.current = [];
        };
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        if (!files.length) return;

        let validFiles = files;
        let hasInvalidExt = false;
        let hasOversized = false;

        if (allowedExtensions.length > 0) {
            validFiles = validFiles.filter((file) => {
                const ext = "." + file.name.split(".").pop()?.toLowerCase();
                const isValid = allowedExtensions.includes(ext);
                if (!isValid) hasInvalidExt = true;
                return isValid;
            });
        }

        if (maxFileSize) {
            validFiles = validFiles.filter((file) => {
                const isValid = file.size <= maxFileSize;
                if (!isValid) hasOversized = true;
                return isValid;
            });
        }

        const currentCount = images?.length || 0;
        const remaining = maxCount - currentCount;
        const countExceeded = validFiles.length > remaining;

        if (hasInvalidExt || hasOversized || countExceeded) {
            const parts: string[] = [];
            
            if (hasInvalidExt) {
                const friendlyFormats = allowedExtensions.map(ext => ext.replace('.', '').toUpperCase()).join(', ');
                parts.push(`Some files have invalid formats (allowed: ${friendlyFormats})`);
            }
            if (hasOversized) {
                const mbSize = Math.round(maxFileSize >= 1000000 ? maxFileSize / 1000000 : maxFileSize / (1024 * 1024));
                parts.push(`File or some files exceed ${mbSize}MB`);
            }
            if (countExceeded) {
                parts.push(`You can only add ${remaining} ${remaining < maxCount ? "more " : ""}image${remaining !== 1 ? "s" : ""}`);
            }

            let errorMsg = "";
            if (parts.length > 1) {
                const lastPart = parts.pop();
                errorMsg = `${parts.join(', ')}. ${lastPart}.`;
            } else if (parts.length === 1) {
                errorMsg = `${parts[0]}.`;
            }

            // Capitalize the first letter of the final message
            errorMsg = errorMsg.charAt(0).toUpperCase() + errorMsg.slice(1);

            showSnackBar(errorMsg, "error");
        }

        if (countExceeded) {
            validFiles = validFiles.slice(0, remaining);
        }

        if (validFiles.length > 0) {
            const updated = multiple
                ? [...(images || []), ...validFiles]
                : validFiles.slice(0, 1);
            onChange(updated);
        }

        e.target.value = "";
    };

    const handleRemove = (index: number) => {
        if (onRemoveRequest) {
            onRemoveRequest(index);
            return;
        }
        if (!images) return;
        const updated = images.filter((_, i) => i !== index);
        onChange(updated.length ? updated : []);
    };

    const openFileDialog = () => {
        if (!disabled) inputRef.current?.click();
    };

    const getFileName = (item: File | string): string =>
        typeof item === "string"
            ? item.split("/").pop() || "image"
            : item.name;

    const renderAddPlaceholder = (key: string | number) => (
        <Box
            key={key}
            sx={{
                width: size,
                height: size,
                border: "2px dashed #ccc",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: disabled ? "not-allowed" : "pointer",
                transition: "0.2s",
                "&:hover": !disabled
                    ? { borderColor: "#1976d2", bgcolor: "#f5faff" }
                    : {},
                position: "relative",
                ...sx,
            }}
            onClick={openFileDialog}
        >
            <AddAPhoto sx={{ fontSize: size / 2.5, color: "#888" }} />
        </Box>
    );

    const renderPreview = (item: File | string, index: number) => {
        const key = `${typeof item === "string" ? item : `${item.name}-${item.lastModified}`}-${index}`;
        console.log("Rendering preview for key:", key, "item:", item, "index:", index);
        const src = failed[key] ? FALLBACK_IMG : getImageUrl(item);

        const filename = getFileName(item);

        return (
            <Box
                key={key}
                sx={{
                    width: size,
                    height: size,
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: onImageClick ? "pointer" : "default",
                    border: "1px solid #e0e0e0",
                    "&:hover .remove-btn": { opacity: 1 },
                    ...sx,
                }}
            >
                <Avatar
                    src={src}
                    variant="square"
                    slotProps={{
                        img: {
                            onError: () => setFailed((p) => ({ ...p, [key]: true })),
                        },
                    }}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                    }}
                    onClick={() => onImageClick?.(index)}
                />

                {showRemoveButton && !isReadOnly && (
                    <IconButton
                        className="remove-btn"
                        size="small"
                        onClick={() => handleRemove(index)}
                        sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "#fff",
                            opacity: 0,
                            transition: "opacity 0.2s",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                )}

                <Tooltip title={filename}>
                    <Typography
                        variant="caption"
                        sx={{
                            position: "absolute",
                            bottom: 2,
                            left: 0,
                            width: "100%",
                            textAlign: "center",
                            color: "#fff",
                            fontSize: "0.7rem",
                            background: "rgba(0,0,0,0.4)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            px: 0.5,
                        }}
                    >
                        {filename}
                    </Typography>
                </Tooltip>
            </Box>
        );
    };

    const canAddMore = multiple && (!images || images.length < maxCount);

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
            }}
        >
            {images?.map(renderPreview)}
            {((canAddMore || !multiple) && !isReadOnly) && renderAddPlaceholder("add-btn")}
            <input
                type="file"
                accept={accept}
                multiple={multiple}
                ref={inputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
                disabled={isReadOnly}
            />
        </Box>
    );
};

export default ImageUploader;
